import type { DetailedHTMLProps, ImgHTMLAttributes } from 'react'
import { useState, useEffect, useCallback, useMemo } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { Icon } from '@/components/icon'
import { cn } from '@/infra'

type ImageData = { uri: string; nome: string }

type ZoomableImageProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  images: ImageData[]
  initialIndex: number
  isSelectionMode: boolean
  isSelected: boolean
  onSelect: () => void
}

const SelectedOverlay = () => (
  <div className="pointer-events-none absolute inset-0 border-x-12 border-y-8 border-neutral-200" />
)

const SelectionCheckbox = ({ isSelected }: { isSelected: boolean }) => (
  <div
    className={cn(
      'absolute top-3 left-3 flex h-6 w-6 items-center justify-center rounded border-2 bg-white shadow-sm',
      isSelected ? 'border-primary bg-primary' : 'border-gray-300',
    )}
  >
    {isSelected && <Icon name="check" size={16} className="text-white" />}
  </div>
)

export const ZoomableImage = ({
  src,
  alt,
  images,
  initialIndex,
  isSelectionMode,
  isSelected,
  onSelect,
}: ZoomableImageProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const hasNavigation = useMemo(() => images.length > 1, [images])
  const currentImage = useMemo(() => images[currentIndex], [images, currentIndex])
  const totalImages = images.length

  const goToPrevious = useCallback(() => {
    if (!hasNavigation) return
    setCurrentIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1))
  }, [hasNavigation, totalImages])

  const goToNext = useCallback(() => {
    if (!hasNavigation) return
    setCurrentIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1))
  }, [hasNavigation, totalImages])

  const handleImageClick = useCallback(() => {
    if (isSelectionMode) onSelect()
  }, [isSelectionMode, onSelect])

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      if (isSelectionMode) return
      setIsOpen(open)
    },
    [isSelectionMode],
  )

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious()
      if (e.key === 'ArrowRight') goToNext()
      if (e.key === 'Escape') setIsOpen(false)
    }

    globalThis.addEventListener('keydown', handleKeyDown)
    return () => globalThis.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, hasNavigation, goToPrevious, goToNext, setIsOpen])

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOnOpenChange}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            'relative w-full cursor-pointer overflow-hidden outline-none',
            !isSelectionMode && 'hover:scale-105',
          )}
          onClick={handleImageClick}
        >
          <img
            src={src}
            alt={alt}
            className="aspect-video h-auto w-full object-cover transition-transform select-none"
            draggable={false}
          />
          {isSelected && <SelectedOverlay />}
          {isSelectionMode && <SelectionCheckbox isSelected={isSelected} />}
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4">
          <div className="relative inline-flex items-center justify-center">
            <img
              src={currentImage?.uri}
              alt={currentImage?.nome}
              className="block h-auto max-h-[96vh] w-auto max-w-[96vw] object-contain select-none md:max-h-[94vh] md:max-w-[94vw]"
              draggable={false}
            />

            <DialogPrimitive.Close className="absolute top-0 right-0 flex h-12 w-12 items-center justify-center bg-white/90 hover:bg-white focus:outline-none">
              <Icon name="close" className="h-7 w-7 text-neutral-800" />
            </DialogPrimitive.Close>

            {hasNavigation && (
              <>
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="absolute top-1/2 left-4 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 transition-opacity hover:bg-white focus:outline-none"
                  aria-label="Previous image"
                >
                  <Icon name="chevron-left" className="h-7 w-7 text-neutral-800" />
                </button>

                <button
                  type="button"
                  onClick={goToNext}
                  className="absolute top-1/2 right-4 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 transition-opacity hover:bg-white focus:outline-none"
                  aria-label="Next image"
                >
                  <Icon name="chevron-right" className="h-7 w-7 text-neutral-800" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1">
                  <span className="text-sm text-white">
                    {currentIndex + 1} / {totalImages}
                  </span>
                </div>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
